import { extendTheme } from '@chakra-ui/react';
import { tableAnatomy } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers } from '@chakra-ui/react';

// Create helpers for the table component styling
const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(tableAnatomy.keys);

// Define the "propvetter" table variant with softer brand colors
const propvetterVariant = definePartsStyle({
  table: {
    borderRadius: "xl",
    boxShadow: "none",
    border: "1px solid",
    borderColor: "#E2E8F0", 
    overflow: "hidden",
    bg: "white",
    width: "100%",
    fontFamily: "'IBM Plex Sans', Arial, sans-serif",
  },
  th: {
    bg: "white",
    color: "#334155",
    fontWeight: "600",
    fontSize: "sm",
    py: "3",
    px: "4",
    borderBottom: "1px solid",
    borderColor: "#E2E8F0",
    textTransform: "none",
    letterSpacing: "normal",
    transition: "all 0.2s",
    position: "relative",
    cursor: "pointer",
    _hover: {
      bg: "#F8FAFC",
    },
    // Column group header styles
    '&[data-is-group-header="true"]': {
      bg: "#F1F5F9",
      fontWeight: "600",
      fontSize: "sm",
      color: "#334155",
      textAlign: "center",
      py: "3",
      borderBottom: "1px solid",
      borderColor: "#E2E8F0",
      '&:first-of-type': {
        borderTopLeftRadius: "xl",
      },
      '&:last-of-type': {
        borderTopRightRadius: "xl",
      },
    },
    // Sorted column styles
    '&[data-is-sorted="true"]': {
      bg: "#F1F5F9",
      color: "#334155",
      borderBottomColor: "#3B82F6",
      borderBottomWidth: "2px",
    },
    // Centered column styles
    '&[data-is-numeric="true"]': {
      textAlign: "center",
    },
    // Top-left corner
    '&:first-of-type': {
      borderTopLeftRadius: "xl",
    },
    // Top-right corner
    '&:last-of-type': {
      borderTopRightRadius: "xl",
    },
  },
  td: {
    py: "3",
    px: "4",
    borderColor: "#E2E8F0",
    fontSize: "sm",
    color: "#334155",
    bg: "white",
    // Centered column styles
    '&[data-is-numeric="true"]': {
      textAlign: "center",
    },
    // Totals row styles
    'tr[data-is-total="true"] &': {
      fontWeight: "600",
      bg: "#F1F5F9",
      color: "#334155",
      borderTop: "1px solid",
      borderTopColor: "#E2E8F0",
      '&:first-of-type': {
        borderBottomLeftRadius: "xl",
      },
      '&:last-of-type': {
        borderBottomRightRadius: "xl",
      },
    },
    // Bottom-left corner
    'tr:last-child &:first-of-type': {
      borderBottomLeftRadius: "xl",
    },
    // Bottom-right corner
    'tr:last-child &:last-of-type': {
      borderBottomRightRadius: "xl",
    },
  },
  tr: {
    _hover: {
      bg: "#F8FAFC",
    },
    // Totals row styles
    '&[data-is-total="true"]': {
      _hover: {
        bg: "#F1F5F9",
      },
    },
  },
  caption: {
    bg: "white",
    borderTop: "1px solid",
    borderTopColor: "#E2E8F0",
    py: "4",
    px: "4",
    color: "#64748B",
  },
  tfoot: {
    tr: {
      _hover: { bg: "#F1F5F9" },
      _focus: { bg: "#F1F5F9" },
    },
    th: {
      borderTop: "1px solid",
      borderTopColor: "#E2E8F0",
      bg: "#F8FAFC",
      color: "#334155",
      fontWeight: "600",
    },
  },
});

// Define the table component configuration
const tableStyles = defineMultiStyleConfig({
  variants: { propvetter: propvetterVariant },
  defaultProps: { variant: "propvetter" },
});

// Extend the existing theme
const theme = extendTheme({
  styles: {
    global: {
      body: {
        bg: '#F1F5F9',
        minH: '100vh'
      },
      '.editable-cell': {
        cursor: 'default'
      }
    }
  },
  components: {
    Table: tableStyles,
    Button: {
      defaultProps: {
        colorScheme: 'blue'
      }
    },
    Input: {
      defaultProps: {
        size: 'lg'
      }
    }
  }
});

export default theme;